import type { DateTime } from 'luxon'
import { useMemo } from 'react'

import type { FormattedRow } from '@/features/bessDashboard/types'
import type { ForecastMarketPair } from '@/features/bidding/types/forecastPricing'
import { MARKET_PAIR_OPTIONS } from '@/features/bidding/types/forecastPricing'
import { getMarketTimezone } from '@/features/bidding/utils/forecast/convertForecastTimezone'
import {
  extractHourlyQuantilePrices,
  getMissingForecastHours,
} from '@/features/bidding/utils/forecast/extractHourlyQuantilePrices'
import { getMarketProgramNames, isSymmetricMarket } from '@/features/bidding/utils/forecast/marketProgramNames'

export type MarketAvailability = {
  market: ForecastMarketPair
  hasData: boolean
  isComplete: boolean
  /** Available hours out of expected total */
  availableHours: number
  expectedHours: number
}

export type UseForecastMarketAvailabilityParams = {
  forecastsData: FormattedRow[] | undefined
  selectedDate: DateTime
}

export type UseForecastMarketAvailabilityResult = {
  /** Availability status for each market */
  marketAvailability: Map<ForecastMarketPair, MarketAvailability>
  /** Markets that have no data at all */
  unavailableMarkets: Set<ForecastMarketPair>
  /** Markets that have incomplete data (some but not all hours) */
  incompleteMarkets: Set<ForecastMarketPair>
}

/**
 * Hook to check forecast data availability for all market pairs.
 * Used to disable market dropdown options when data is unavailable.
 */
export function useForecastMarketAvailability({
  forecastsData,
  selectedDate,
}: UseForecastMarketAvailabilityParams): UseForecastMarketAvailabilityResult {
  return useMemo(() => {
    const marketAvailability = new Map<ForecastMarketPair, MarketAvailability>()
    const unavailableMarkets = new Set<ForecastMarketPair>()
    const incompleteMarkets = new Set<ForecastMarketPair>()

    const marketTimezone = getMarketTimezone()

    for (const option of MARKET_PAIR_OPTIONS) {
      const market = option.value
      const marketPrograms = getMarketProgramNames(market)
      const isSymmetric = isSymmetricMarket(market)

      // Check UP direction
      const upPrices = forecastsData
        ? extractHourlyQuantilePrices(forecastsData, marketPrograms.up, selectedDate, marketTimezone)
        : null

      // For asymmetric markets, also check DOWN direction
      const downPrices =
        !isSymmetric && forecastsData
          ? extractHourlyQuantilePrices(forecastsData, marketPrograms.down, selectedDate, marketTimezone)
          : null

      // Determine availability
      // For symmetric markets: check UP only
      // For asymmetric markets: need BOTH UP and DOWN to be available
      const hasUpData = upPrices !== null && upPrices.hourlyPrices.size > 0
      const hasDownData = isSymmetric ? true : downPrices !== null && downPrices.hourlyPrices.size > 0
      const hasData = hasUpData && hasDownData

      // Determine completeness
      const upMissingHours = upPrices ? getMissingForecastHours(upPrices) : []
      const downMissingHours = !isSymmetric && downPrices ? getMissingForecastHours(downPrices) : []
      const isUpComplete = hasUpData && upMissingHours.length === 0
      const isDownComplete = isSymmetric ? true : hasDownData && downMissingHours.length === 0
      const isComplete = isUpComplete && isDownComplete

      // Calculate available hours (use UP for simplicity, or minimum of both for asymmetric)
      const upAvailable = upPrices?.hourlyPrices.size ?? 0
      const downAvailable = !isSymmetric ? (downPrices?.hourlyPrices.size ?? 0) : upAvailable
      const availableHours = Math.min(upAvailable, downAvailable)
      const expectedHours = upPrices?.dayHours ?? downPrices?.dayHours ?? 24

      const availability: MarketAvailability = {
        market,
        hasData,
        isComplete,
        availableHours,
        expectedHours,
      }

      marketAvailability.set(market, availability)

      if (!hasData) {
        unavailableMarkets.add(market)
      } else if (!isComplete) {
        incompleteMarkets.add(market)
      }
    }

    return {
      marketAvailability,
      unavailableMarkets,
      incompleteMarkets,
    }
  }, [forecastsData, selectedDate])
}
